/**
 * services/EntryModelService.ts
 * Service for managing entry models
 *
 * Entry models define specific entry methodologies for trades, such as
 * "Market order at break", "Limit at support", or "Scale in thirds".
 * They can optionally be organized under EntryModelCategories.
 *
 * Extends BaseService to provide CRUD operations with status-based filtering
 * and category-based queries.
 */

import { BaseService } from './BaseService';
import type { EntryModel } from '@/types/schema/EntryModel';
import type { EntityStatus } from '@/types/shared/EntityStatus';
import { isEntityActive } from '@/types/shared/EntityStatus';

/**
 * Service for managing entry models
 *
 * Provides CRUD operations plus status-based and category-based filtering.
 * Inherits getAll(), get(), update(), softDelete() from BaseService.
 */
export class EntryModelService extends BaseService<EntryModel> {
  constructor() {
    super('entry_models');
  }

  /**
   * Add a new entry model with default status
   *
   * Defaults to status: 'active' if not provided.
   *
   * @param data - Partial entry model data
   * @returns Promise resolving to the created entry model
   */
  override async add(data: Partial<EntryModel>): Promise<EntryModel> {
    return super.add({ status: 'active', ...data });
  }

  /**
   * Get entry models by status
   *
   * Filters entry models by the specified EntityStatus.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @param status - The status to filter by ('active' | 'inactive' | 'archived')
   * @returns Promise resolving to array of entry models with matching status
   */
  async getByStatus(status: EntityStatus): Promise<EntryModel[]> {
    const allEntryModels = await this.getAll();
    return allEntryModels.filter(entryModel => entryModel.status === status);
  }

  /**
   * Get all active entry models
   *
   * Convenience method for getting entry models where status is 'active'.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @returns Promise resolving to array of active entry models
   */
  async getActiveEntryModels(): Promise<EntryModel[]> {
    const allEntryModels = await this.getAll();
    return allEntryModels.filter(entryModel => isEntityActive(entryModel.status));
  }

  /**
   * Get entry models by category
   *
   * Filters entry models by entryModelCategoryId.
   * If categoryId is undefined, returns uncategorized items (where category is undefined).
   *
   * @param categoryId - The category ID to filter by, or undefined for uncategorized items
   * @returns Promise resolving to array of entry models in the specified category
   */
  async getByCategoryId(categoryId: string | undefined): Promise<EntryModel[]> {
    const allEntryModels = await this.getAll();
    return allEntryModels.filter(entryModel => entryModel.entryModelCategoryId === categoryId);
  }
}

// Export singleton instance
export const entryModelService = new EntryModelService();
